import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";

const DB = "conecta-capturas";
const STORE = "queue";
let dbPromise = null;
const openDb = () => {
  dbPromise = dbPromise || new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "client_id" });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => { dbPromise = null; reject(r.error); };
  });
  return dbPromise;
};
const run = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
  });
};
export const queueAll = () => run("readonly", (s) => s.getAll());
const put = (item) => run("readwrite", (s) => s.put(item));
export const removeQueued = async (id) => { await run("readwrite", (s) => s.delete(id)); emit(); };

const listeners = new Set();
const emit = async () => { const items = await queueAll(); listeners.forEach((l) => l(items)); };

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export async function enqueueCapture({ kind, blob, text, filename }) {
  const item = {
    client_id: uuid(), kind, text: text || null, filename: filename || null, mime: blob?.type || null,
    data: blob ? await blob.arrayBuffer() : null, captured_at: new Date().toISOString(), attempts: 0, status: "pendiente", next_at: 0,
  };
  await put(item);
  emit();
  syncQueue();
  return item.client_id;
}

let syncing = false;
export async function syncQueue(force = false) {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  try {
    for (const it of await queueAll()) {
      if (!force && (it.status === "error" || it.next_at > Date.now())) continue;
      await put({ ...it, status: "subiendo" });
      emit();
      try {
        const fd = new FormData();
        fd.append("client_id", it.client_id);
        fd.append("kind", it.kind);
        fd.append("captured_at", it.captured_at);
        if (it.text) fd.append("text", it.text);
        if (it.data) fd.append("file", new Blob([it.data], { type: it.mime }), it.filename);
        const { data } = await api.post("/captures", fd, { timeout: 120000 });
        await run("readwrite", (s) => s.delete(it.client_id));
        window.dispatchEvent(new CustomEvent("capture:uploaded", { detail: data }));
      } catch (e) {
        const code = e.response?.status;
        const attempts = it.attempts + 1;
        const permanent = code && code >= 400 && code < 500 && ![401, 408, 429].includes(code);
        await put({ ...it, status: permanent ? "error" : "pendiente", attempts, last_error: errMsg(e), next_at: Date.now() + Math.min(60000, 2000 * 2 ** attempts) });
      }
    }
  } finally {
    syncing = false;
    emit();
  }
}

let started = false;
export function startQueue() {
  if (started) return;
  started = true;
  window.addEventListener("online", () => syncQueue(true));
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && syncQueue());
  setInterval(() => syncQueue(), 10000);
  syncQueue(true);
}

export function useQueue() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    listeners.add(setItems);
    queueAll().then(setItems).catch(() => {});
    return () => listeners.delete(setItems);
  }, []);
  return items;
}

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}
